import { Box, Grid, Hidden, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { FC } from 'react';
import { DrawerModal } from 'components/atoms/careers';
import { SubmitButton, U1TextField } from 'components/atoms';
import { DropzoneIcon, FileUploaderIcon } from 'svgs/careers';
import { DropzoneArea } from 'material-ui-dropzone';

export interface UploaderDrawerProps {
  open: boolean;
  onClose: () => void;
  onScrollEnd?: () => Promise<void>;
  onSubmit: () => void;
  onSelectFile: (files: File[]) => void;
  onChangeLink: (link: string) => void;
  linkValue: string;
  file: File;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 900,
    '& .MuiDropzoneArea-active': {
      backgroundColor: 'rgba(224, 98, 30, 0.05)',
      border: `dashed ${theme.palette.primary.main}`,
      backgroundImage: 'none',
      backgroundSize: '80%',
    },
    '& .MuiSnackbar-anchorOriginBottomLeft': {
      '& .MuiPaper-root': {
        '& .MuiSnackbarContent-action': {
          marginTop: -72,
          marginRight: -21,
          [theme.breakpoints.up('sm')]: {
            marginTop: 0,
          },
        },
      },
    },
  },
  icon: {
    margin: theme.spacing(7, 0),
    padding: theme.spacing(4, 3, 4, 3.5),
    backgroundColor: theme.palette.background.default,
    maxWidth: 96,
    maxHeight: 96,
  },
  title: {
    color: '#232D3D',
    fontWeight: 500,
    marginBottom: theme.spacing(7),
  },
  titleUp: {
    color: '#232D3D',
    fontWeight: 500,
    textTransform: 'uppercase',
    margin: theme.spacing(6, 0, 3),
  },
  size: {
    color: '#929395',
    marginTop: -6,
    marginBottom: 12,
    [theme.breakpoints.up('sm')]: {
      marginTop: 50,
      marginBottom: 0,
    },
  },
  dropZone: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 0,
    minHeight: 330,
    '& .MuiDropzoneArea-textContainer': {
      position: 'relative',
      '& p': {
        fontWeight: 500,
        color: theme.palette.primary.main,
        position: 'absolute',
        top: theme.spacing(36),
        left: 0,
        right: 0,
        fontSize: 15,
        maxWidth: 211,
        margin: '0 auto',
      },
      '& svg': {
        height: 'auto',
        width: 'auto',
        marginTop: theme.spacing(12),
      },
    },
    '& .MuiDropzonePreviewList-root': {
      marginTop: theme.spacing(20),
      marginBottom: theme.spacing(6),
      justifyContent: 'center',
      '&:hover': {
        '& button': {
          '& span': {
            '& svg': {
              '& path': {
                fill: theme.palette.primary.main,
              },
            },
          },
        },
      },
    },
  },

  typeText: {
    fontWeight: 500,
    color: '#232d3d',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  text: {
    color: '#929395',
    maxWidth: 211,
    margin: '0 auto',
  },
  box: {
    top: theme.spacing(23),
    left: 0,
    right: 0,
  },
}));
const UploaderDrawer: FC<UploaderDrawerProps> = ({ open, onClose, onSubmit, onSelectFile, onChangeLink, linkValue, file }) => {
  const classes = useStyles();
  const expression = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
  const regex = new RegExp(expression);

  return (
    <DrawerModal open={open} onClose={onClose}>
      <form className={classes.root}>
        <Box className={classes.icon}>
          <FileUploaderIcon />
        </Box>
        <Typography variant="h2" className={classes.title}>
          Upload your attachments
        </Typography>
        <U1TextField
          label="Link(URL)"
          onChange={(e) => {
            onChangeLink(e.target.value);
          }}
          value={linkValue}
        />
        <Grid direction="row" justify="space-between" container>
          <Grid xs={12} sm="auto" item>
            <Typography variant="h6" className={classes.titleUp}>
              Or drag and drop your files here.
            </Typography>
          </Grid>
          <Hidden xsDown>
            <Grid xs={12} sm="auto" item>
              <Typography className={classes.size}>Maximum size: 5MB</Typography>
            </Grid>
          </Hidden>
        </Grid>
        <Hidden smUp>
          <Box display="flex" justifyContent="flex-end">
            <Typography className={classes.size}>Maximum size: 5MB</Typography>
          </Box>
        </Hidden>
        <Box position="relative">
          <DropzoneArea
            acceptedFiles={[
              'image/jpeg',
              'image/jpg',
              'image/png',
              'image/gif',
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ]}
            onChange={onSelectFile}
            showFileNames
            filesLimit={1}
            maxFileSize={500000}
            dropzoneClass={classes.dropZone}
            // @ts-expect-error
            Icon={DropzoneIcon}
            dropzoneText={'clicking here'}
          />
          <Box position="absolute" className={classes.box}>
            <Typography className={classes.typeText} align="center">
              .PDF .JPG .PNG .DOC
            </Typography>
            <Typography className={classes.text} align="center">
              You can also upload files by
            </Typography>
          </Box>
        </Box>
        <Box mt={5} mb={18}>
          <SubmitButton onClick={onSubmit} disabled={!linkValue.match(regex) && !file}>
            Confirm
          </SubmitButton>
        </Box>
      </form>
    </DrawerModal>
  );
};

export default UploaderDrawer;
